// A COR DE UM ROTULO — os dois circulos e a tira de paleta, numa peca so ([DA-674]).
//
// Pedido dele em 04/09/2026: *"dois circulos depois do rotulo. O primeiro e' referente ao fundo e
// o segundo a fonte de cada rotulo... A exibicao dos quadrados e' so para Admin."*
//
// ⚠️ **O PAR fundo+fonte e' especificacao dele, nao enfeite** — mesma da [DA-592], onde eu entreguei
// so o contorno e custou duas voltas. As duas cores andam juntas em toda a peca.
//
// ⛔ **A tira de paleta NAO e' dropdown flutuante, de proposito.** Ela nasce no fluxo, logo abaixo
// da grade de cartoes. Dentro de um modal o `.modal-body` e' `overflow-y: auto`, entao painel
// `position: absolute` sai CORTADO — o jeito flutuante da casa exige `fixed` + `getBoundingClientRect`
// (é o que o `<ComboBox>` faz e o `<AutocompleteNCM>` nao faz). Uma tira no fluxo nao tem esse
// problema em nenhuma altura de janela, e ainda ganha algo que o dropdown nao daria: ela DIZ qual
// rotulo e qual dos dois campos esta sendo pintado.
import { X } from 'lucide-react';

export type CampoCor = 'fundo' | 'fonte';

/**
 * AS FAMILIAS DE COR, cada uma com tres intensidades.
 *
 * ⚠️ **Por que a paleta virou familia+intensidade em 04/09/2026.** Ela nasceu com 20 amostras de
 * UMA intensidade so (os `*-500` do Tailwind), e ele pediu *"mais cores: Vermelho, Amarelo, Azul,
 * Azul escuro, Rosa"*. Quatro das cinco JA existiam — o pedido, lido inteiro, dizia duas coisas ao
 * mesmo tempo: faltava o eixo ESCURO/CLARO (o `Azul escuro` era o unico ausente de verdade), e as
 * que existiam **nao estavam sendo encontradas**. Ele confirmou as duas.
 *
 * ⛔ **Os 20 hex antigos continuam TODOS aqui, na coluna do meio.** Sao exatamente os `medio` de
 * cada familia mais os neutros. Sumir com um deles apagaria uma escolha ja gravada — o `AGUARDANDO`
 * dele ja estava em `#84cc16` quando esta lista foi reescrita.
 *
 * ⚠️ **A ORDEM das tres e' clara → media → escura, sempre.** A posicao passa a ensinar a
 * intensidade: quem procura "Azul escuro" olha a coluna da direita da linha do Azul, sem precisar
 * ler. Pra quem nao separa verde de vermelho ([[user_daltonismo]]), posicao e nome sao o sinal —
 * a bolinha, sozinha, nunca foi.
 */
export const FAMILIAS_ROTULO = [
  { nome: 'Vermelho', claro: '#fca5a5', medio: '#ef4444', escuro: '#991b1b' },
  { nome: 'Laranja', claro: '#fdba74', medio: '#f97316', escuro: '#9a3412' },
  { nome: 'Âmbar', claro: '#fcd34d', medio: '#f59e0b', escuro: '#92400e' },
  { nome: 'Amarelo', claro: '#fde047', medio: '#eab308', escuro: '#854d0e' },
  { nome: 'Verde-limão', claro: '#bef264', medio: '#84cc16', escuro: '#3f6212' },
  { nome: 'Verde', claro: '#86efac', medio: '#22c55e', escuro: '#166534' },
  { nome: 'Verde-esmeralda', claro: '#6ee7b7', medio: '#10b981', escuro: '#065f46' },
  { nome: 'Verde-água', claro: '#5eead4', medio: '#14b8a6', escuro: '#115e59' },
  { nome: 'Ciano', claro: '#67e8f9', medio: '#06b6d4', escuro: '#155e75' },
  { nome: 'Azul-celeste', claro: '#7dd3fc', medio: '#0ea5e9', escuro: '#075985' },
  { nome: 'Azul', claro: '#93c5fd', medio: '#3b82f6', escuro: '#1e40af' },
  { nome: 'Azul-índigo', claro: '#a5b4fc', medio: '#6366f1', escuro: '#3730a3' },
  { nome: 'Violeta', claro: '#c4b5fd', medio: '#8b5cf6', escuro: '#5b21b6' },
  { nome: 'Roxo', claro: '#d8b4fe', medio: '#a855f7', escuro: '#6b21a8' },
  { nome: 'Magenta', claro: '#f0abfc', medio: '#d946ef', escuro: '#86198f' },
  { nome: 'Rosa', claro: '#f9a8d4', medio: '#ec4899', escuro: '#9d174d' },
  { nome: 'Cereja', claro: '#fda4af', medio: '#f43f5e', escuro: '#9f1239' },
] as const;

/** Os sem-matiz. Ficam numa linha propria: eles nao tem "familia", e misturar confundiria a grade. */
export const NEUTROS_ROTULO = [
  { nome: 'Branco', hex: '#ffffff' },
  { nome: 'Cinza claro', hex: '#cbd5e1' },
  { nome: 'Cinza', hex: '#64748b' },
  { nome: 'Cinza escuro', hex: '#334155' },
  { nome: 'Preto', hex: '#000000' },
] as const;

/** Toda cor da paleta, achatada — e' o que responde "qual e' o nome deste hex?". */
export const CORES_ROTULO: ReadonlyArray<{ hex: string; nome: string }> = [
  ...FAMILIAS_ROTULO.flatMap((f) => [
    { hex: f.claro, nome: `${f.nome} claro` },
    { hex: f.medio, nome: f.nome },
    { hex: f.escuro, nome: `${f.nome} escuro` },
  ]),
  ...NEUTROS_ROTULO.map((n) => ({ hex: n.hex, nome: n.nome })),
];

/**
 * O nome de uma cor gravada. Hex fora da paleta cai no proprio hex — nao inventar nome pro que a
 * casa nao nomeou, e nao esconder o valor: cor que veio de fora ainda precisa se identificar.
 */
export function nomeDaCor(hex: string | null | undefined): string {
  if (!hex) return 'sem cor';
  return CORES_ROTULO.find((c) => c.hex === hex.toLowerCase())?.nome ?? hex;
}

/** Como o rotulo se pinta. Nulo dos dois lados = ele ainda nao escolheu, e o cartao fica neutro. */
export function pinturaDoRotulo(fundo: string | null | undefined, fonte: string | null | undefined) {
  return {
    backgroundColor: fundo || 'var(--input-bg)',
    color: fonte || 'var(--input-fg)',
  };
}

/**
 * A mesma pintura, mas devolvendo SO o que foi escolhido — pra quem ja tem estilo proprio.
 *
 * ⚠️ A diferenca pra de cima nao e' cosmetica: a caixa da coluna veste `btn-secundario`, e devolver
 * o token de fallback ali SOBRESCREVERIA o fundo da classe em toda linha, inclusive nas que ninguem
 * pintou. Aqui a ausencia de cor tem que ser ausencia de propriedade, nao um valor equivalente.
 */
export function pinturaSeEscolhida(fundo: string | null | undefined, fonte: string | null | undefined) {
  return {
    ...(fundo ? { backgroundColor: fundo } : {}),
    ...(fonte ? { color: fonte } : {}),
  };
}

/**
 * Os dois circulos. So o admin os ve — mas a COR que eles definem aparece pra todo mundo.
 *
 * ⚠️ Circulo vazio (sem cor escolhida) fica TRACEJADO e transparente, nunca branco: branco e' uma
 * cor valida da paleta, e pintar o "nao escolhido" igual ao escolhido-branco confundiria os dois
 * estados — o mesmo defeito do placeholder que e' um valor valido do campo ([DA-608]).
 */
export function CirculosCor({
  fundo, fonte, campoAberto, onAbrir, nome,
}: {
  fundo: string | null | undefined;
  fonte: string | null | undefined;
  campoAberto: CampoCor | null;
  onAbrir: (campo: CampoCor | null) => void;
  nome: string;
}) {
  const circulo = (campo: CampoCor, cor: string | null | undefined) => {
    // O circulo tambem DIZ qual cor esta ali. Sem isso ele exibe a escolha num sinal que ele nao
    // consegue ler — e' o mesmo pedido das amostras da paleta, aplicado a bolinha que as resume.
    const legenda = `${campo === 'fundo' ? 'Cor de fundo' : 'Cor da fonte'} de ${nome} — ${nomeDaCor(cor)}`;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onAbrir(campoAberto === campo ? null : campo); }}
        title={legenda}
        aria-label={legenda}
        style={{
          width: 15, height: 15, borderRadius: '9999px', flex: '0 0 auto', padding: 0,
          backgroundColor: cor || 'transparent',
          // O anel preto e' o mesmo sinal de "este esta aberto" da regua de chips do AppCel
          // ([DA-592]) — quando tudo e' colorido, quem diz "escolhido" e' a FORMA ([DA-469]).
          border: cor ? '1px solid rgba(0,0,0,0.35)' : '1px dashed rgba(0,0,0,0.35)',
          outline: campoAberto === campo ? '2px solid #111827' : 'none',
          outlineOffset: 1,
        }}
      />
    );
  };
  return (
    <span className="flex items-center gap-1 px-1" style={{ flex: '0 0 auto' }}>
      {circulo('fundo', fundo)}
      {circulo('fonte', fonte)}
    </span>
  );
}

/** Uma amostra. Peca minuscula, mas usada nas duas grades — nao repetir o anel em dois lugares. */
function Amostra({ hex, nome, escolhida, onEscolher }: {
  hex: string; nome: string; escolhida: boolean; onEscolher: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onEscolher}
      title={nome}
      aria-label={nome}
      className="rounded-full"
      style={{
        width: 18, height: 18, padding: 0, flex: '0 0 auto', backgroundColor: hex,
        border: '1px solid rgba(0,0,0,0.25)',
        outline: escolhida ? '2px solid #111827' : 'none',
        outlineOffset: 1,
      }}
    />
  );
}

/**
 * A tira que abre embaixo da grade. Diz em palavras o que esta pintando — sem isso, dois circulos
 * iguais em oito cartoes viram adivinhacao sobre qual deles abriu.
 *
 * ⚠️ **O NOME de cada familia fica ESCRITO, nao so no hover** (pedido dele, 04/09/2026). Legenda de
 * `title` exige passar o mouse amostra por amostra — com 56 cores isso e' um jogo de adivinhacao, e
 * pior pra quem nao distingue verde de vermelho. Escrito uma vez por familia, o nome custa uma
 * coluna e resolve as tres intensidades de uma vez.
 */
export function TiraPaleta({
  nome, campo, valor, onEscolher, onFechar,
}: {
  nome: string;
  campo: CampoCor;
  valor: string | null | undefined;
  onEscolher: (cor: string | null) => void;
  onFechar: () => void;
}) {
  return (
    <div
      className="col-span-2 rounded-lg p-2 mt-1"
      style={{ border: '1px solid var(--border-color, #d1d5db)', backgroundColor: 'var(--page-bg)' }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold">
          {campo === 'fundo' ? 'Fundo' : 'Fonte'} de <strong>{nome}</strong>
          <span className="font-normal ml-1" style={{ color: 'var(--text-muted)' }}>
            · hoje: {nomeDaCor(valor)}
          </span>
        </span>
        <button
          type="button"
          onClick={onFechar}
          title="Fechar a paleta"
          aria-label="Fechar a paleta"
          className="text-gray-400 hover:text-blue-600"
          style={{ background: 'none', border: 'none', padding: 2 }}
        >
          <X size={13} />
        </button>
      </div>

      {/* Uma LINHA por familia: as tres intensidades em ordem fixa (clara · media · escura) e o
          nome logo em seguida. A posicao ensina a intensidade sem precisar ler.

          ⚠️ **As bolinhas vem ANTES do nome, e o nome NAO estica.** A 1a versao punha o nome a
          esquerda com `flex: 1`, o que jogava as amostras pra borda direita da celula — a ~170px
          do proprio nome e **coladas no nome da familia seguinte**. Medido, estava tudo certo (56
          amostras, sem rolagem); foi o PRINT que mostrou que a leitura associava cada trio ao
          rotulo errado ([DA-619]: a metrica responde "cabe?", nunca "esta certo?"). Com o nome
          depois e sem esticar, a distancia ate o proprio rotulo (6px) fica bem menor que a ate o
          vizinho (o `gap-x` de 20px), e a duvida some sem precisar de borda nem de cor. */}
      <div className="grid gap-y-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', columnGap: 20 }}>
        {FAMILIAS_ROTULO.map((f) => (
          <div key={f.nome} className="flex items-center" style={{ gap: 4 }}>
            <Amostra hex={f.claro} nome={`${f.nome} claro`} escolhida={valor === f.claro} onEscolher={() => onEscolher(f.claro)} />
            <Amostra hex={f.medio} nome={f.nome} escolhida={valor === f.medio} onEscolher={() => onEscolher(f.medio)} />
            <Amostra hex={f.escuro} nome={`${f.nome} escuro`} escolhida={valor === f.escuro} onEscolher={() => onEscolher(f.escuro)} />
            <span className="text-[11px] truncate" style={{ flex: '0 1 auto', minWidth: 0, marginLeft: 2 }}>{f.nome}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px solid var(--border-color, #d1d5db)' }}>
        {NEUTROS_ROTULO.map((n) => (
          <span key={n.hex} className="flex items-center gap-1.5">
            <Amostra hex={n.hex} nome={n.nome} escolhida={valor === n.hex} onEscolher={() => onEscolher(n.hex)} />
            <span className="text-[11px]">{n.nome}</span>
          </span>
        ))}
        {/* Voltar ao neutro tem que ser alcancavel: sem esta porta, uma cor escolhida por engano
            ficaria pra sempre — recusa sem saida e' o defeito da [DA-560]. */}
        <button
          type="button"
          onClick={() => onEscolher(null)}
          className="text-[11px] px-2 py-0.5 rounded ml-auto"
          style={{
            border: '1px dashed var(--border-color, #d1d5db)',
            backgroundColor: 'transparent',
            color: 'var(--text-muted)',
            outline: !valor ? '2px solid #111827' : 'none',
            outlineOffset: 1,
          }}
        >
          sem cor
        </button>
      </div>
    </div>
  );
}
